type HelpContent = string;
type AsyncContentLoader = (id: string) => Promise<HelpContent | null>;

class HelpContentProvider {
  private static instance: HelpContentProvider;
  private contentMap: Map<string, HelpContent>;
  private asyncLoader?: AsyncContentLoader;

  private constructor() {
    this.contentMap = new Map();
  }

  public static getInstance(): HelpContentProvider {
    if (!HelpContentProvider.instance) {
      HelpContentProvider.instance = new HelpContentProvider();
    }
    return HelpContentProvider.instance;
  }

  public registerContent(id: string, content: HelpContent): void {
    this.contentMap.set(id, content);
  }

  public registerBulk(contentMap: Record<string, HelpContent>): void {
    Object.entries(contentMap).forEach(([id, content]) => {
      this.registerContent(id, content);
    });
  }

  public getContent(id: string): HelpContent | null {
    return this.contentMap.has(id) ? this.contentMap.get(id)! : null;
  }

  public async getContentAsync(id: string, options: { skipCache?: boolean } = {}): Promise<HelpContent | null> {
    const { skipCache = false } = options;

    // Check local cache first (unless skipCache is true)
    if (!skipCache) {
      const cachedContent = this.getContent(id);
      if (cachedContent !== null) {
        return cachedContent;
      }
    }

    // If async loader is set, try to load content
    if (this.asyncLoader) {
      const content = await this.asyncLoader(id);
      if (content !== null) {
        // Only cache if skipCache is false
        if (!skipCache) {
          this.registerContent(id, content);
        }
        return content;
      }
    }

    return null;
  }

  public hasContent(id: string): boolean {
    return this.contentMap.has(id);
  }

  public getAllIds(): string[] {
    return Array.from(this.contentMap.keys());
  }

  public clearContent(): void {
    this.contentMap.clear();
  }

  public setAsyncLoader(loader: AsyncContentLoader): void {
    this.asyncLoader = loader;
  }

  public clearAsyncLoader(): void {
    this.asyncLoader = undefined;
  }

  /**
   * Configure the provider to load guide content from Firestore
   * @param guideService - GuideService instance for fetching guides
   */
  public setGuideService(guideService: any): void {
    this.setAsyncLoader(async (guideKey: string) => {
      try {
        // Query guide by key field
        const guides = await guideService.query('key', '==', guideKey);

        if (guides.length === 0) {
          console.warn(`[HelpContentProvider] Guide not found: ${guideKey}`);
          return null;
        }

        const guide = guides[0];

        // Return first content item's description as preview
        if (guide.content && guide.content.length > 0) {
          const firstItem = guide.content[0];
          return firstItem.description || firstItem.title || null;
        }

        return null;
      } catch (error) {
        console.error('[HelpContentProvider] Failed to load guide:', error);
        return null;
      }
    });
  }
}

export default HelpContentProvider;